const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const SENTRY_REPLAY_SESSION_SAMPLE_RATE = 0.02;
const SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE = 0.2;

/**
 * Errors we drop on the floor before they reach Sentry. Each entry is a
 * documented source of recurring noise that has no actionable signal:
 *
 *  - PGRST303: PostgREST "JWT expired" — supabase-js auto-refreshes and the
 *    next request succeeds. Surfacing the transient one wastes triage budget.
 *  - "Failed to fetch dynamically imported module": chunk-not-found during
 *    deploy windows. Resolves on the next page load (the reload handler in
 *    main.tsx already refreshes the user when this hits the global handler).
 *  - "unsafe-eval is not an allowed source": CSP block from third-party SDKs.
 *    The CSP is intentional; the SDK feature degrades gracefully.
 */
const IGNORED_ERROR_PATTERNS: readonly RegExp[] = [
  /\bPGRST303\b/i,
  /\bJWT expired\b/i,
  /Failed to fetch dynamically imported module/i,
  /unsafe-eval.{0,3}is not an allowed source/i,
];

function matchesIgnoredPattern(text: string): boolean {
  return IGNORED_ERROR_PATTERNS.some((re) => re.test(text));
}

export function isIgnoredException(error: unknown): boolean {
  if (!error) return false;
  if (error instanceof Error) {
    if (error.message && matchesIgnoredPattern(error.message)) return true;
    if (error.name && matchesIgnoredPattern(error.name)) return true;
    return false;
  }
  if (typeof error === "string") {
    return matchesIgnoredPattern(error);
  }
  if (typeof error === "object") {
    const record = error as Record<string, unknown>;
    const candidates = [record.message, record.code, record.name, record.error].filter(
      (v): v is string => typeof v === "string",
    );
    return candidates.some(matchesIgnoredPattern);
  }
  return false;
}

let sentryModule: typeof import("@sentry/react") | null = null;

interface NormalizedCapturedException {
  error: Error;
  extra?: Record<string, unknown>;
}

export interface ObservabilityIdentity {
  userId: string;
  email?: string | null;
  tier?: "starter" | "pro" | null;
  isAdmin?: boolean | null;
}

export function buildSentryObservabilityContext(identity: ObservabilityIdentity | null) {
  if (!identity) {
    return {
      user: null,
      tags: {
        tier: "anonymous",
        is_admin: "false",
      },
      contexts: {
        account: null,
      },
    };
  }

  const emailDomain = identity.email?.trim().split("@")[1]?.trim().toLowerCase() || null;

  return {
    user: {
      id: identity.userId,
    },
    tags: {
      tier: identity.tier ?? "unknown",
      is_admin: String(identity.isAdmin ?? false),
    },
    contexts: {
      account: {
        tier: identity.tier ?? "unknown",
        is_admin: identity.isAdmin ?? false,
        email_domain: emailDomain,
      },
    },
  };
}

async function loadSentry() {
  if (!sentryModule) {
    sentryModule = await import("@sentry/react");
  }
  return sentryModule;
}

function buildErrorFromRecord(record: Record<string, unknown>): NormalizedCapturedException {
  const message =
    (typeof record.message === "string" && record.message.trim()) ||
    (typeof record.error === "string" && record.error.trim()) ||
    null;
  const keys = Object.keys(record).sort();
  const error = new Error(
    message || `Non-Error exception object with keys: ${keys.join(", ") || "none"}`,
  );
  const name =
    (typeof record.name === "string" && record.name.trim()) ||
    (typeof record.code === "string" && record.code.trim()) ||
    "Error";
  error.name = name;
  if (typeof record.stack === "string" && record.stack.trim()) {
    error.stack = record.stack;
  }

  const extra = { ...record };
  delete extra.name;
  delete extra.message;
  delete extra.error;
  delete extra.stack;

  return {
    error,
    extra: Object.keys(extra).length > 0 ? extra : undefined,
  };
}

export function normalizeCapturedException(error: unknown): NormalizedCapturedException {
  if (error instanceof Error) {
    return { error };
  }

  if (typeof error === "string" && error.trim()) {
    return { error: new Error(error) };
  }

  if (error && typeof error === "object") {
    return buildErrorFromRecord(error as Record<string, unknown>);
  }

  return { error: new Error(String(error)) };
}

export function initSentry(): void {
  if (!SENTRY_DSN) return;

  const run = async () => {
    const Sentry = await loadSentry();
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: import.meta.env.MODE,
      enabled: import.meta.env.PROD,
      tracesSampleRate: 0.2,
      replaysSessionSampleRate: SENTRY_REPLAY_SESSION_SAMPLE_RATE,
      replaysOnErrorSampleRate: SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE,
      beforeSend: (event, hint) => {
        if (hint?.originalException && isIgnoredException(hint.originalException)) {
          return null;
        }
        const message = event.message;
        if (typeof message === "string" && matchesIgnoredPattern(message)) {
          return null;
        }
        const exceptionValues = event.exception?.values ?? [];
        for (const ex of exceptionValues) {
          if (typeof ex.value === "string" && matchesIgnoredPattern(ex.value)) {
            return null;
          }
          if (typeof ex.type === "string" && matchesIgnoredPattern(ex.type)) {
            return null;
          }
        }
        return event;
      },
    });
  };

  if (typeof requestIdleCallback !== "undefined") {
    requestIdleCallback(
      () => {
        run().catch(() => {});
      },
      { timeout: 5000 },
    );
  } else {
    setTimeout(() => {
      run().catch(() => {});
    }, 0);
  }
}

export async function captureException(error: unknown, context?: Record<string, unknown>) {
  if (!SENTRY_DSN) return;
  if (isIgnoredException(error)) return;
  const Sentry = await loadSentry();
  const normalized = normalizeCapturedException(error);
  const mergedContext =
    normalized.extra && Object.keys(normalized.extra).length > 0
      ? {
          ...(context ?? {}),
          extra: {
            ...((context?.extra as Record<string, unknown> | undefined) ?? {}),
            normalized_error: normalized.extra,
          },
        }
      : context;
  Sentry.captureException(normalized.error, mergedContext);
}

export async function syncSentryIdentity(identity: ObservabilityIdentity | null): Promise<void> {
  if (!SENTRY_DSN) return;

  const Sentry = await loadSentry();
  const { user, tags, contexts } = buildSentryObservabilityContext(identity);

  Sentry.setUser(user);
  Sentry.setTag("tier", tags.tier);
  Sentry.setTag("is_admin", tags.is_admin);

  if (contexts.account) {
    Sentry.setContext("account", contexts.account);
  } else {
    Sentry.setContext("account", null);
  }
}
